<script setup lang="ts">
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from "vue";
import Icon from "./Icon.vue";
import Dropdown, { type MenuItem } from "./Dropdown.vue";
import { fitTabs } from "../composables/fitTabs";
import { t } from "../i18n";

/**
 * The inbox's filter pills. After the views come the company's tags, as WhatsApp Web's lists.
 * The row never scrolls: what does not fit goes to the caret button at the end, as WhatsApp's,
 * whose menu also holds `addLabel` (a new tag) when given (2026-10-28).
 */
const props = defineProps<{
  /** `group` sets pills apart in the caret's menu (the views, then the tags). */
  tabs: Array<{ id: string; label: string; count?: number; group?: string }>;
  addLabel?: string;
}>();
const emit = defineEmits<{ add: [] }>();
const model = defineModel<string>({ default: "" });

const row = ref<HTMLElement>();
const measure = ref<HTMLElement>();
const fit = ref<{ shown: number[]; hidden: number[] }>({
  shown: props.tabs.map((_, i) => i),
  hidden: [],
});
const menu = ref({ open: false, x: 0, y: 0 });

const shown = computed(() =>
  fit.value.shown.map((i) => props.tabs[i]).filter((x) => x !== undefined),
);
const items = computed<MenuItem[]>(() => {
  const hidden: MenuItem[] = [];
  let group: string | undefined;
  for (const i of fit.value.hidden) {
    const tab = props.tabs[i];
    if (!tab) continue;
    if (hidden.length && tab.group !== group)
      hidden.push({ label: "", separator: true });
    group = tab.group;
    hidden.push({
      label: tab.label,
      ...(tab.count ? { count: tab.count } : {}),
      action: () => (model.value = tab.id),
    });
  }
  const add = props.addLabel
    ? [{ label: props.addLabel, icon: "wa-plus", action: () => emit("add") }]
    : [];
  return hidden.length && add.length
    ? [...hidden, { label: "", separator: true }, ...add]
    : [...hidden, ...add];
});

/** Lays the row out from the hidden copy's pill widths. */
function layout() {
  const r = row.value,
    m = measure.value;
  if (!r || !m) return;
  const style = getComputedStyle(r);
  const pills = [...m.querySelectorAll<HTMLElement>("[data-pill]")];
  const caret = m.querySelector<HTMLElement>("[data-more]");
  fit.value = fitTabs({
    widths: pills.map((p) => p.offsetWidth),
    active: props.tabs.findIndex((x) => x.id === model.value),
    room:
      r.clientWidth -
      parseFloat(style.paddingLeft) -
      parseFloat(style.paddingRight),
    gap: parseFloat(style.columnGap) || 0,
    more: caret?.offsetWidth ?? 0,
    alwaysMore: Boolean(props.addLabel),
  });
}

function openMenu(event: MouseEvent) {
  const r = (event.currentTarget as HTMLElement).getBoundingClientRect();
  menu.value = { open: true, x: r.left, y: r.bottom + 4 };
}

let observer: ResizeObserver | null = null;
onMounted(() => {
  layout();
  observer = new ResizeObserver(() => layout());
  if (row.value) observer.observe(row.value);
  if (measure.value) observer.observe(measure.value);
  void document.fonts?.ready.then(layout);
});
onBeforeUnmount(() => observer?.disconnect());
watch(
  () => [props.tabs, model.value, props.addLabel],
  () => void nextTick(layout),
  { deep: true },
);
</script>
<template>
  <div ref="row" class="chat-filters view-tabs" role="tablist">
    <button
      v-for="tab in shown"
      :key="tab.id"
      role="tab"
      class="chip"
      :class="{ active: model === tab.id }"
      :aria-selected="model === tab.id"
      @click="model = tab.id"
    >
      {{ tab.label }}<span v-if="tab.count">{{ tab.count }}</span>
    </button>
    <button
      v-if="items.length"
      type="button"
      class="chip filter-plus"
      :aria-label="t('common.more')"
      :title="t('common.more')"
      aria-haspopup="menu"
      :aria-expanded="menu.open"
      @click="openMenu"
    >
      <Icon name="wa-drop-down" :size="20" />
    </button>
    <div ref="measure" class="view-tabs-measure" aria-hidden="true">
      <span v-for="tab in tabs" :key="tab.id" data-pill class="chip"
        >{{ tab.label }}<span v-if="tab.count">{{ tab.count }}</span></span
      ><span data-more class="chip filter-plus"
        ><Icon name="wa-drop-down" :size="20"
      /></span>
    </div>
    <Dropdown
      :open="menu.open"
      :x="menu.x"
      :y="menu.y"
      :items="items"
      @close="menu.open = false"
    />
  </div>
</template>
